/**
 * DraggableButton.tsx - Individual Draggable Button Component
 * 
 * Features:
 * - Drag and drop reordering (via Draggr)
 * - Status indicators
 * - Action buttons
 * - Custom styling
 * - Actr logging
 * - Backtrackr undo/redo
 * 
 * ARCHITECTURE:
 * - Uses @opcentral/draggr for drag and drop
 * - Uses @opcentral/actr for action logging
 * - Uses @opcentral/backtrackr for undo/redo
 */

import React from 'react';
import { useDraggable, useDroppable } from '@opcentral/draggr';
import { useBacktrackr } from '@opcentral/backtrackr';
import type { DraggableButtonProps } from './types';

export const DraggableButton: React.FC<DraggableButtonProps> = ({
  button,
  index,
  isActive,
  customLabel,
  showActions = true,
  defaultActions = ['duplicate', 'edit', 'external'],
  onClick,
  onReorder,
  onDuplicate,
  onEdit,
  onRemove,
  onExternal,
  variant = 'default',
  className = '',
}) => {
  const backtrackr = useBacktrackr() as any;

  // Make button draggable using Draggr
  const dragProps = useDraggable(button.id, {
    type: 'button',
    data: { button, index },
    onDragStart: () => {
      actr?.execute('buttonbar:drag-start', { 
        buttonId: button.id, 
        index 
      });
    },
    onDragEnd: () => {
      actr?.execute('buttonbar:drag-end', { 
        buttonId: button.id 
      });
    }
  });

  // Make button a drop zone using Draggr
  const dropProps = useDroppable(`button-${button.id}`, {
    accepts: ['button'],
    onDrop: (source: any) => {
      if (source.data.index !== index) {
        const oldIndex = source.data.index;
        const newIndex = index;
        
        // Record for undo/redo
        if (backtrackr) {
          backtrackr.recordChange({
            type: 'reorder-buttons',
            description: `Reorder button from ${oldIndex} to ${newIndex}`,
            context: null,
            before: { index: oldIndex },
            after: { index: newIndex },
            metadata: { buttonId: source.data.button.id }
          });
        }
        
        // Log action
        actr?.execute('buttonbar:reorder', {
          from: oldIndex,
          to: newIndex,
          buttonId: source.data.button.id
        });
        
        // Execute reorder
        onReorder(oldIndex, newIndex);
      }
    }
  });

  const isDragging = dragProps.isDragging;
  const isOver = dropProps.isOver;

  // Click handler
  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking action buttons
    if ((e.target as HTMLElement).closest('.button-action')) {
      return;
    }
    onClick(button);
  };

  // Action handlers
  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const displayLabel = customLabel || button.label;
  const statusClass = button.status ? `status-${button.status}` : '';
  const variantClass = `variant-${variant}`;
  const activeClass = isActive ? 'active' : '';
  const disabledClass = button.disabled ? 'disabled' : '';
  const draggingClass = isDragging ? 'dragging' : '';
  const overClass = isOver ? 'drag-over' : '';

  // Combine refs (with guards for when DraggrProvider is not available)
  const setRefs = React.useCallback(
    (node: HTMLButtonElement | null) => {
      if (dragProps.setNodeRef) dragProps.setNodeRef(node);
      if (dropProps.setNodeRef) dropProps.setNodeRef(node);
    },
    [dragProps, dropProps]
  );

  return (
    <button
      ref={setRefs}
      className={`draggable-button ${variantClass} ${activeClass} ${statusClass} ${disabledClass} ${draggingClass} ${overClass} ${className}`}
      onClick={handleClick}
      disabled={button.disabled}
      title={button.tooltip}
      data-button-id={button.id}
      style={button.customStyles}
      {...dragProps.attributes}
      {...dragProps.listeners}
    >
      {/* Icon */}
      {button.icon && (
        <span className="button-icon">{button.icon}</span>
      )}

      {/* Label */}
      <span className="button-label">{displayLabel}</span>

      {/* Badge */}
      {button.badge && (
        <span className="button-badge">{button.badge}</span>
      )}

      {/* Status Indicator */}
      {button.status && (
        <span className={`button-status ${button.status}`} />
      )}

      {/* Action Buttons */}
      {showActions && !button.disabled && (
        <span className="button-actions">
          {/* Duplicate */}
          {defaultActions.includes('duplicate') && onDuplicate && (
            <span
              className="button-action button-duplicate"
              onClick={(e) => handleAction(e, () => onDuplicate(button.id))}
              title="Duplicate"
            >
              ⊕
            </span>
          )}

          {/* Edit */}
          {defaultActions.includes('edit') && onEdit && (
            <span
              className="button-action button-edit"
              onClick={(e) => handleAction(e, () => onEdit(button.id))}
              title="Edit"
            >
              ✎
            </span>
          )}

          {/* Remove */}
          {defaultActions.includes('remove') && onRemove && (
            <span
              className="button-action button-remove"
              onClick={(e) => handleAction(e, () => onRemove(button.id))}
              title="Remove"
            >
              ✕
            </span>
          )}

          {/* External */}
          {defaultActions.includes('external') && onExternal && button.href && (
            <span
              className="button-action button-external"
              onClick={(e) => handleAction(e, () => onExternal(button))}
              title="Open in new window"
            >
              ↗
            </span>
          )}

          {/* Custom Actions */}
          {button.actions?.map((action) => {
            const shouldShow = !action.show || action.show(button);
            if (!shouldShow) return null;

            return (
              <span
                key={action.id}
                className={`button-action button-custom-${action.id}`}
                onClick={(e) => handleAction(e, () => action.handler(button))}
                title={action.label}
              >
                {action.icon || '•'}
              </span>
            );
          })}
        </span>
      )}
    </button>
  );
};
